import { useMemo, useState } from 'react';
import { cn } from '@mlm/ui';
import type { Membership } from '../../../lib/api';
import { PairDropsEditor, type PairDropRow, PairingCombinationsEditor } from '../binary-editor';
import { NumberField, ToggleField } from '../fields';

/**
 * Per-tier Extreme Binary settings. Each RECEIVER membership tier supplies its
 * own pairing config under `config.computation.binary` (`BinaryBlock` in
 * @mlm/shared): a `pairing_combination` threshold→payout table plus the full
 * `pair_drops` milestone system (arbitrary-N cash/GC bonuses, replacing the
 * legacy `fifth_pair`/`every_pairs_gc`) and a per-tier
 * `total_flushout` toggle. Pairing is per-membership — a tier with no
 * `pairing_combination` earns no pairing (the Extreme form has no flat table).
 * Also edits the top-level `config.binary_direct_requirements` (the side-change
 * directs gate override; empty = inherit the plan-level default).
 *
 * The other `BinaryBlock` keys (`points`, `pairs_per_day`, `daily_income_limit`,
 * `max_strong_leg`) are managed elsewhere (per-tier cycle limits) and are
 * preserved untouched here. Tabs pick one tier at a time because each tier owns
 * a variable-length combination table.
 */
type Block = Record<string, unknown>;

/** Threshold→payout Record → sorted rows for `PairingCombinationsEditor`. */
function pcToRows(pc: unknown): Array<{ points: number; payout: number }> {
  if (!pc || typeof pc !== 'object') return [];
  return Object.entries(pc as Record<string, number | string>)
    .map(([points, payout]) => ({ points: Number(points) || 0, payout: Number(payout) || 0 }))
    .sort((a, b) => a.points - b.points);
}

/** Rows → threshold→payout Record (last row wins on a duplicate threshold). */
function rowsToPc(rows: Array<{ points: number; payout: number }>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of rows) out[String(r.points)] = r.payout;
  return out;
}

export function BinaryPairingPerTier({
  memberships,
  draft,
  onChange,
  readOnly,
}: {
  memberships: Membership[];
  draft: Record<string, Record<string, unknown>>;
  onChange: (id: string, next: Record<string, unknown>) => void;
  readOnly: boolean;
}) {
  const [activeId, setActiveId] = useState<string>(memberships[0]?.id ?? '');
  const active = useMemo(
    () => memberships.find((m) => m.id === activeId) ?? memberships[0],
    [memberships, activeId],
  );

  function block(cfg: Record<string, unknown>): Block {
    const computation = (cfg.computation as Block | undefined) ?? {};
    return (computation.binary as Block | undefined) ?? {};
  }

  function writeBlock(id: string, nextBlock: Block) {
    const current = draft[id] ?? {};
    const computation = { ...((current.computation as Block | undefined) ?? {}) };
    const cleaned: Block = {};
    for (const [k, v] of Object.entries(nextBlock)) {
      if (v === undefined) continue;
      if (v && typeof v === 'object' && Object.keys(v as object).length === 0) continue;
      cleaned[k] = v;
    }
    if (Object.keys(cleaned).length === 0) delete computation.binary;
    else computation.binary = cleaned;
    const next = { ...current };
    if (Object.keys(computation).length === 0) delete next.computation;
    else next.computation = computation;
    onChange(id, next);
  }

  function setPairing(id: string, rows: Array<{ points: number; payout: number }>) {
    const b = { ...block(draft[id] ?? {}) };
    if (rows.length === 0) delete b.pairing_combination;
    else b.pairing_combination = rowsToPc(rows);
    writeBlock(id, b);
  }

  function getPairDrops(cfg: Record<string, unknown>): PairDropRow[] {
    const rows = block(cfg).pair_drops;
    return Array.isArray(rows) ? (rows as PairDropRow[]) : [];
  }
  function setPairDrops(id: string, rows: PairDropRow[]) {
    const b = { ...block(draft[id] ?? {}) };
    if (rows.length === 0) delete b.pair_drops;
    else b.pair_drops = rows;
    writeBlock(id, b);
  }

  function getToggle(cfg: Record<string, unknown>, key: string): boolean {
    return block(cfg)[key] === true;
  }
  function setToggle(id: string, key: string, on: boolean) {
    const b = { ...block(draft[id] ?? {}) };
    if (!on) delete b[key];
    else b[key] = true;
    writeBlock(id, b);
  }

  // `binary_direct_requirements` is a TOP-LEVEL membership-config key (not under
  // `computation.binary`). Empty = inherit the plan-level default from the
  // Extreme form; a number (incl. 0 = no gate) overrides it for this tier.
  function getDirects(cfg: Record<string, unknown>): number | '' {
    const v = cfg.binary_direct_requirements;
    return typeof v === 'number' ? v : '';
  }
  function setDirects(id: string, v: number | null) {
    const current = { ...(draft[id] ?? {}) };
    if (v === null) delete current.binary_direct_requirements;
    else current.binary_direct_requirements = Math.max(0, Math.floor(v));
    onChange(id, current);
  }

  if (memberships.length === 0 || !active) {
    return (
      <p className="text-xs text-muted-foreground border rounded-md p-4">
        No membership tiers configured yet. Create them under Members → Memberships first.
      </p>
    );
  }

  const activeCfg = draft[active.id] ?? {};
  const hasPerTierTable = pcToRows(block(activeCfg).pairing_combination).length > 0;

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <p className="text-xs font-medium">Per-tier Extreme Binary (pairing + side gate)</p>
        <p className="text-[10px] text-muted-foreground">
          Each RECEIVER tier runs its own pairing table + GC riders + directs gate. Pairing is
          per-membership — a tier with no table here earns no pairing (there is no flat fallback).
          Pick a tier to edit it.
        </p>
      </div>

      <div className="flex items-center gap-1 flex-wrap">
        {memberships.map((m) => {
          const filled = pcToRows(block(draft[m.id] ?? {}).pairing_combination).length > 0;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => setActiveId(m.id)}
              title={m.name}
              className={cn(
                'h-8 px-3 rounded-md text-xs font-mono border transition-colors',
                m.id === active.id
                  ? 'bg-primary/10 border-primary text-primary'
                  : 'border-input text-muted-foreground hover:bg-muted',
              )}
            >
              {m.code}
              {filled ? <span className="ml-1 text-primary">•</span> : null}
            </button>
          );
        })}
      </div>

      <div className="border rounded-md p-4 space-y-4">
        <div className="space-y-2">
          <p className="text-[10px] text-muted-foreground">
            Each combination needs that many points on BOTH legs to form one pair (matched
            largest-first). Empty = this tier earns no pairing (pairing is per-membership; there is
            no flat fallback).
          </p>
          <PairingCombinationsEditor
            value={pcToRows(block(activeCfg).pairing_combination)}
            disabled={readOnly}
            onChange={(rows) => setPairing(active.id, rows)}
          />
        </div>

        <div className="space-y-2">
          <p className="text-xs font-medium">Pair drops (milestone bonuses)</p>
          <p className="text-[10px] text-muted-foreground -mt-1">
            Each row fires on every Nth PAID pair for this tier (N=1 = every pair). It can pay a
            cash and/or GC bonus and change what happens to the normal pairing payout on that pair.
            Leave empty for no milestone bonuses. N must be unique per row.
          </p>
          <PairDropsEditor
            value={getPairDrops(activeCfg)}
            disabled={readOnly}
            onChange={(rows) => setPairDrops(active.id, rows)}
          />
        </div>

        <ToggleField
          label="Total flushout (zero BOTH legs on a cap flush)"
          hint="For this tier's earners, when a cap flush hits any pair this event, burn BOTH legs' post-pairing remainder to 0. Overrides the flat Extreme total-flushout setting."
          disabled={readOnly}
          value={getToggle(activeCfg, 'total_flushout')}
          onChange={(on) => setToggle(active.id, 'total_flushout', on)}
        />

        <div className="border-t pt-4">
          <NumberField
            label="Directs to unlock side change (this tier)"
            hint="Overrides the plan-level default for this tier. Leave EMPTY to inherit the default; 0 = no gate (members of this tier can change their side immediately)."
            value={getDirects(activeCfg)}
            disabled={readOnly}
            min={0}
            step={1}
            placeholder="inherit plan default"
            onChange={(v) => setDirects(active.id, v)}
          />
        </div>

        {!hasPerTierTable ? (
          <p className="text-[10px] text-muted-foreground">
            <span className="font-mono">{active.code}</span> has no per-tier pairing table, so it
            earns NO pairing — there is no flat fallback, pairing is per-membership. Add a table
            above to pay this tier.
          </p>
        ) : null}
      </div>
    </div>
  );
}
